import React, { useState, useEffect, useCallback } from "react";
import {
  exportLeaderboardCsv,
  fetchTestSeriesAPI,
  fetchLeaderboardAPI,
  fetchQuestionStatsAPI,
  fetchTestAttemptsHistoryAPI,
  fetchReviewAnswersAPI,
} from "../../utils/api";
import { showError } from "../../utils/sweetAlert";
import MixedContentRenderer from "../../components/MixedContentRenderer";
import { EmptyState, ErrorAlert, LoadingState, Pagination, SearchField, StatCard } from "../../components/ui";
import "./Results.css";

const ITEMS_PER_PAGE = 9;

const QUESTION_TYPE_LABELS = {
  SINGLE_CORRECT: "Single correct",
  MCQ: "Single correct",
  MULTI_CORRECT: "Multiple correct",
  NUMERICAL: "Numerical",
  MATCH: "Match",
  PARAGRAPH: "Paragraph",
  RANGE: "Range",
};

// Numbers from the API may arrive as strings or null
const num = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};
const pct = (value) => `${num(value).toFixed(1)}%`;

const scoreTone = (percentage) => {
  const p = num(percentage);
  if (p >= 90) return "excellent";
  if (p >= 75) return "good";
  if (p >= 60) return "average";
  return "poor";
};

const formatDate = (value, withTime = false) => {
  if (!value) return "—";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "—";
  return withTime
    ? date.toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : date.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

const durationBetween = (start, end) => {
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "—";
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m ${s}s`;
};

// The API has used wrong/skipped (and the student site unattempted); the panel uses one set of names.
// partial: a multiple-correct answer with only some of the correct options (it earns part of the marks)
const normalizeStatus = (status) => {
  if (status === "correct") return "correct";
  if (status === "partial") return "partial";
  if (status === "incorrect" || status === "wrong") return "incorrect";
  return "unanswered";
};

// Badge class, icon and label of each review status
const STATUS_BADGE = {
  correct: { badge: "badge-success", icon: "bi-check-lg", label: "Correct" },
  partial: { badge: "badge-warning", icon: "bi-circle-half", label: "Partially correct" },
  incorrect: { badge: "badge-danger", icon: "bi-x-lg", label: "Incorrect" },
  unanswered: { badge: "badge-neutral", icon: "bi-dash", label: "Not attempted" },
};

// MATCH answers: { matches: { A: "Q", B: "P", … } } (Column I → Column II)
const matchesOf = (answer) =>
  answer && typeof answer === "object" && answer.matches && typeof answer.matches === "object" ? answer.matches : {};

const answerKeys = (answer) => {
  if (answer === null || answer === undefined) return [];
  if (Array.isArray(answer)) return answer.map(String);
  if (typeof answer === "object") return [];
  return [String(answer)];
};

const formatAnswer = (answer) => {
  if (answer === null || answer === undefined || answer === "") return "Not attempted";
  if (Array.isArray(answer)) return answer.length ? answer.join(", ") : "Not attempted";
  if (typeof answer === "object") {
    if (answer.value !== undefined && answer.value !== null) return String(answer.value);
    // RANGE: "2-5"; NUMERICAL without an exact value: its accepted ranges
    if (answer.range) return `${answer.range}${answer.min !== answer.max ? " (inclusive)" : ""}`;
    if (Array.isArray(answer.ranges)) {
      return answer.ranges.length ? answer.ranges.map((r) => `${r.min} to ${r.max}`).join(", ") : "—";
    }
    if (answer.matches) {
      const pairs = Object.entries(answer.matches);
      return pairs.length ? pairs.map(([a, b]) => `${a} → ${b}`).join(", ") : "Not attempted";
    }
    return "—";
  }
  return String(answer);
};

function Leaderboard() {
  const [testSeriesList, setTestSeriesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // view: "list" | "leaderboard" | "attempts" | "review"
  const [view, setView] = useState("list");
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState("");

  const [selectedTestSeries, setSelectedTestSeries] = useState(null);
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [attemptsHistory, setAttemptsHistory] = useState([]);
  const [selectedAttempt, setSelectedAttempt] = useState(null);
  const [reviewData, setReviewData] = useState([]);
  const [reviewSections, setReviewSections] = useState([]);
  const [reviewFilter, setReviewFilter] = useState("all");
  const [questionStats, setQuestionStats] = useState(null); // { total_attempts, data }
  const [exporting, setExporting] = useState(false);

  const loadTestSeries = useCallback(async () => {
    try {
      setLoading(true);
      setListError("");
      const response = await fetchTestSeriesAPI();
      if (response?.success && Array.isArray(response.data)) {
        setTestSeriesList(response.data);
      } else if (response) {
        setListError(response.message || "Failed to load test series.");
      }
    } catch (error) {
      console.error("Error fetching test series:", error);
      setListError("Couldn't reach the server to load test series.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTestSeries();
  }, [loadTestSeries]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const goTo = (nextView) => {
    setView(nextView);
    setViewError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleShowLeaderboard = async (testSeries) => {
    setSelectedTestSeries(testSeries);
    setLeaderboardData([]);
    goTo("leaderboard");
    setViewLoading(true);
    try {
      const response = await fetchLeaderboardAPI(testSeries.id);
      setLeaderboardData(response?.success && Array.isArray(response.leaderboard) ? response.leaderboard : []);
    } catch (error) {
      console.error("Error fetching leaderboard:", error);
      setViewError("Couldn't load the leaderboard. Please try again.");
    } finally {
      setViewLoading(false);
    }
  };

  const handleViewAttempts = async (student) => {
    setSelectedStudent(student);
    setAttemptsHistory([]);
    goTo("attempts");
    setViewLoading(true);
    try {
      const response = await fetchTestAttemptsHistoryAPI(selectedTestSeries.id, student.user_id);
      setAttemptsHistory(response?.success && Array.isArray(response.history) ? response.history : []);
    } catch (error) {
      console.error("Error fetching attempts history:", error);
      setViewError("Couldn't load this student's attempts. Please try again.");
    } finally {
      setViewLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      await exportLeaderboardCsv(selectedTestSeries.id);
    } catch (error) {
      showError(error.message || "Couldn't download the leaderboard.");
    } finally {
      setExporting(false);
    }
  };

  const handleQuestionStats = async () => {
    setQuestionStats(null);
    goTo("stats");
    setViewLoading(true);
    try {
      const response = await fetchQuestionStatsAPI(selectedTestSeries.id);
      if (response?.success) setQuestionStats(response);
      else if (response) setViewError(response.message || "Couldn't load the question analysis.");
    } catch (error) {
      setViewError("Couldn't load the question analysis. Please try again.");
    } finally {
      setViewLoading(false);
    }
  };

  const handleReviewAnswers = async (attempt) => {
    setSelectedAttempt(attempt);
    setReviewData([]);
    setReviewSections([]);
    setReviewFilter("all");
    goTo("review");
    setViewLoading(true);
    try {
      const response = await fetchReviewAnswersAPI(attempt.attempt_id, selectedStudent.user_id);
      if (response?.success && Array.isArray(response.data)) {
        setReviewData(response.data.map((q) => ({ ...q, status: normalizeStatus(q.status) })));
        setReviewSections(Array.isArray(response.sections) ? response.sections : attempt.sections || []);
      } else {
        setViewError(response?.message || "Couldn't load the answers for this attempt.");
      }
    } catch (error) {
      console.error("Error fetching review answers:", error);
      setViewError("Couldn't load the answers for this attempt.");
    } finally {
      setViewLoading(false);
    }
  };

  // ===================== Review answers =====================
  if (view === "review" && selectedAttempt) {
    const correct = reviewData.filter((q) => q.status === "correct").length;
    const partial = reviewData.filter((q) => q.status === "partial").length;
    const incorrect = reviewData.filter((q) => q.status === "incorrect").length;
    const unanswered = reviewData.length - correct - partial - incorrect;
    const sectionsHavePartial = reviewSections.some((section) => num(section.partial) > 0);
    const visible = reviewData.filter((q) => {
      if (reviewFilter === "all") return true;
      return q.status === reviewFilter;
    });

    return (
      <div className="results-page page">
        <div className="page-header">
          <div>
            <button type="button" className="back-link" onClick={() => goTo("attempts")}>
              <i className="bi bi-arrow-left"></i> Attempts
            </button>
            <h2>Attempt #{selectedAttempt.attempt_no} review</h2>
            <p>
              {selectedStudent?.user_name} · {selectedTestSeries?.title}
            </p>
          </div>
        </div>

        <div className="stats-grid">
          <StatCard
            icon="bi bi-bullseye"
            tone="brand"
            value={`${num(selectedAttempt.score)} / ${num(selectedAttempt.max_marks)}`}
            label="Score"
            hint={pct(selectedAttempt.percentage)}
          />
          <StatCard icon="bi bi-check2-circle" tone="success" value={correct} label="Correct" />
          {partial > 0 && <StatCard icon="bi bi-circle-half" tone="warning" value={partial} label="Partially correct" />}
          <StatCard icon="bi bi-x-circle" tone="danger" value={incorrect} label="Incorrect" />
          <StatCard icon="bi bi-dash-circle" tone="ink" value={unanswered} label="Not attempted" />
        </div>

        {reviewSections.length > 1 && (
          <div className="table-card section-scores">
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Section</th>
                    <th>Score</th>
                    <th>Correct</th>
                    {sectionsHavePartial && <th>Partial</th>}
                    <th>Incorrect</th>
                    <th>Not attempted</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Subject-wise tests: each subject row (its sections' totals) is followed by its sections, indented */}
                  {reviewSections.map((section) => (
                    <tr key={section.section_id} className={section.is_subject ? "section-scores-subject" : undefined}>
                      <td style={section.parent_id ? { paddingLeft: 32 } : undefined}>
                        {section.is_subject && <i className="bi bi-collection text-muted"></i>}{" "}
                        <strong>{section.name || `Section ${section.section_id}`}</strong>
                        {section.is_subject && <span className="text-muted"> · subject total</span>}
                      </td>
                      <td>
                        <strong className="text-strong">{num(section.score)}</strong>
                        <span className="text-muted"> / {num(section.max_marks)}</span>
                      </td>
                      <td>{num(section.correct)}</td>
                      {sectionsHavePartial && <td>{num(section.partial)}</td>}
                      <td>{num(section.wrong)}</td>
                      <td>{Math.max(0, num(section.total_questions) - num(section.answered))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <ErrorAlert message={viewError} onRetry={() => handleReviewAnswers(selectedAttempt)} />

        {viewLoading ? (
          <LoadingState message="Loading answers…" />
        ) : reviewData.length === 0 ? (
          !viewError && <EmptyState icon="bi bi-inbox" title="No answers to show" message="This attempt has no recorded answers." />
        ) : (
          <>
            <div className="toolbar">
              <div className="chip-group">
                {[
                  { key: "all", label: "All", count: reviewData.length },
                  { key: "correct", label: "Correct", count: correct },
                  partial > 0 && { key: "partial", label: "Partially correct", count: partial },
                  { key: "incorrect", label: "Incorrect", count: incorrect },
                  { key: "unanswered", label: "Not attempted", count: unanswered },
                ].filter(Boolean).map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    className={`chip ${reviewFilter === f.key ? "active" : ""}`}
                    onClick={() => setReviewFilter(f.key)}
                  >
                    {f.label} <span className="chip-count">{f.count}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="review-list">
              {visible.map((question) => {
                const qIndex = reviewData.indexOf(question) + 1;
                const state = question.status;
                const badge = STATUS_BADGE[state] || STATUS_BADGE.unanswered;
                const userKeys = answerKeys(question.user_answer);
                const correctKeys = answerKeys(question.correct_answer);
                const isMatch = question.question_type === "MATCH";
                const hasOptions = Array.isArray(question.options) && question.options.length > 0;

                return (
                  <article key={question.question_id ?? qIndex} className={`review-card card ${state}`}>
                    <header className="review-card-header">
                      <div className="review-tags">
                        <span className="review-number">Q{qIndex}</span>
                        <span className={`badge ${badge.badge}`}>
                          <i className={`bi ${badge.icon}`}></i>
                          {badge.label}
                        </span>
                        {question.question_type && (
                          <span className="badge badge-brand">
                            {QUESTION_TYPE_LABELS[question.question_type] || question.question_type}
                          </span>
                        )}
                        {question.section_name && reviewSections.length > 1 && (
                          <span className="badge badge-neutral">
                            <i className="bi bi-layers"></i>{" "}
                            {question.subject_name ? `${question.subject_name} › ` : ""}
                            {question.section_name}
                          </span>
                        )}
                      </div>
                      <div className="review-marks">
                        <span className="text-muted">
                          +{num(question.marks?.positive)} / −{num(question.marks?.negative)}
                        </span>
                        <strong className={num(question.marks?.awarded) < 0 ? "neg" : num(question.marks?.awarded) > 0 ? "pos" : ""}>
                          {num(question.marks?.awarded) > 0 ? "+" : ""}
                          {num(question.marks?.awarded)}
                        </strong>
                      </div>
                    </header>

                    <div className="review-body">
                      {question.paragraph?.en && (
                        <div className="review-paragraph">
                          <MixedContentRenderer content={question.paragraph.en} />
                        </div>
                      )}
                      <div className="review-question">
                        {question.question?.en ? (
                          <MixedContentRenderer content={question.question.en} />
                        ) : (
                          <span className="text-muted">Question text not available</span>
                        )}
                      </div>

                      {isMatch && hasOptions && (
                        <div className="review-match">
                          <table className="data-table">
                            <thead>
                              <tr>
                                <th>Column I</th>
                                <th>Student matched</th>
                                <th>Correct match</th>
                              </tr>
                            </thead>
                            <tbody>
                              {question.options
                                .filter((o) => /^[A-F]$/.test(String(o.option_key)))
                                .map((option) => {
                                  const key = String(option.option_key);
                                  const given = matchesOf(question.user_answer)[key];
                                  const right = matchesOf(question.correct_answer)[key];
                                  const textOf = (k) => question.options.find((o) => String(o.option_key) === k)?.option_en;
                                  return (
                                    <tr key={option.id ?? key}>
                                      <td>
                                        <strong>{key}.</strong> <MixedContentRenderer content={option.option_en || ""} />
                                      </td>
                                      <td className={given ? (given === right ? "pos" : "neg") : "text-muted"}>
                                        {given ? (
                                          <>
                                            <strong>{given}.</strong> <MixedContentRenderer content={textOf(given) || ""} />
                                          </>
                                        ) : (
                                          "—"
                                        )}
                                      </td>
                                      <td>
                                        {right ? (
                                          <>
                                            <strong>{right}.</strong> <MixedContentRenderer content={textOf(right) || ""} />
                                          </>
                                        ) : (
                                          "—"
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {!isMatch && hasOptions && (
                        <div className="review-options">
                          {question.options.map((option) => {
                            const key = String(option.option_key);
                            const picked = userKeys.includes(key);
                            const right = correctKeys.includes(key);
                            return (
                              <div
                                key={option.id ?? key}
                                className={`review-option ${right ? "is-correct" : ""} ${picked && !right ? "is-wrong" : ""}`}
                              >
                                <span className="review-option-key">{key}</span>
                                <div className="review-option-text">
                                  {option.option_en ? (
                                    <MixedContentRenderer content={option.option_en} />
                                  ) : (
                                    <span className="text-muted">—</span>
                                  )}
                                </div>
                                <div className="review-option-tags">
                                  {picked && <span className="badge badge-info">Student's answer</span>}
                                  {right && <span className="badge badge-success">Correct</span>}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {!hasOptions && (
                        <div className="review-answers">
                          <div>
                            <span>Student's answer</span>
                            <strong className={state}>{formatAnswer(question.user_answer)}</strong>
                          </div>
                          <div>
                            <span>Correct answer</span>
                            <strong className="correct">{formatAnswer(question.correct_answer)}</strong>
                          </div>
                        </div>
                      )}

                      {question.explanation?.en && (
                        <details className="review-explanation">
                          <summary>
                            <i className="bi bi-lightbulb"></i> Explanation
                            <i className="bi bi-chevron-down review-caret"></i>
                          </summary>
                          <div className="review-explanation-body">
                            <MixedContentRenderer content={question.explanation.en} />
                          </div>
                        </details>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          </>
        )}
      </div>
    );
  }

  // ===================== Question analysis =====================
  if (view === "stats" && selectedTestSeries) {
    const rows = questionStats?.data || [];
    const hardest = [...rows].filter((r) => r.answered + r.unanswered > 0).sort((a, b) => a.correct_percent - b.correct_percent)[0];

    return (
      <div className="results-page page">
        <div className="page-header">
          <div>
            <button type="button" className="back-link" onClick={() => goTo("leaderboard")}>
              <i className="bi bi-arrow-left"></i> Leaderboard
            </button>
            <h2>Question analysis</h2>
            <p>
              {selectedTestSeries.title} · every submitted attempt ({num(questionStats?.total_attempts)})
            </p>
          </div>
        </div>

        <ErrorAlert message={viewError} onRetry={handleQuestionStats} />

        {viewLoading ? (
          <LoadingState message="Working out question results…" />
        ) : rows.length === 0 ? (
          !viewError && <EmptyState icon="bi bi-bar-chart" title="No questions" message="This test has no questions yet." />
        ) : (
          <>
            {hardest && num(questionStats?.total_attempts) > 0 && (
              <div className="alert alert-info">
                <i className="bi bi-lightning"></i>
                <div className="alert-body">
                  Hardest question: <strong>Q{hardest.number}</strong> ({hardest.section_name}) — {pct(hardest.correct_percent)} of
                  attempts got it right.
                </div>
              </div>
            )}
            <div className="table-card">
              <div className="table-scroll">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Question</th>
                      <th>Section</th>
                      <th>Correct</th>
                      <th title="Multiple-correct answers with only some of the correct options">Partial</th>
                      <th>Incorrect</th>
                      <th>Not attempted</th>
                      <th>% correct</th>
                      <th title="Of the students who answered">Accuracy</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.question_id}>
                        <td className="text-muted">Q{row.number}</td>
                        <td className="stats-question">
                          <div className="stats-question-text">
                            <MixedContentRenderer content={row.question_en || ""} />
                          </div>
                          <small className="text-muted">
                            {QUESTION_TYPE_LABELS[row.question_type] || row.question_type}
                            {row.topic_name || row.topic_name_hi
                              ? ` · ${[row.topic_name, row.topic_name_hi].filter(Boolean).join(" / ")}`
                              : ""}
                          </small>
                        </td>
                        <td>{row.subject_name ? `${row.subject_name} › ${row.section_name}` : row.section_name}</td>
                        <td>{row.correct}</td>
                        <td>{row.question_type === "MULTI_CORRECT" ? num(row.partial) : "—"}</td>
                        <td>{row.incorrect}</td>
                        <td>{row.unanswered}</td>
                        <td>
                          <span className={`score-pill ${scoreTone(row.correct_percent)}`}>{pct(row.correct_percent)}</span>
                        </td>
                        <td>{row.answered ? pct(row.accuracy_percent) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  // ===================== Attempts history =====================
  if (view === "attempts" && selectedStudent) {
    const summary =
      attemptsHistory.length > 0
        ? {
            total: attemptsHistory.length,
            best: Math.max(...attemptsHistory.map((a) => num(a.score))),
            average: (attemptsHistory.reduce((s, a) => s + num(a.score), 0) / attemptsHistory.length).toFixed(1),
            bestPct: Math.max(...attemptsHistory.map((a) => num(a.percentage))),
          }
        : null;

    return (
      <div className="results-page page">
        <div className="page-header">
          <div>
            <button type="button" className="back-link" onClick={() => goTo("leaderboard")}>
              <i className="bi bi-arrow-left"></i> Leaderboard
            </button>
            <h2>{selectedStudent.user_name}</h2>
            <p>All attempts for {selectedTestSeries?.title}</p>
          </div>
        </div>

        <ErrorAlert message={viewError} onRetry={() => handleViewAttempts(selectedStudent)} />

        {summary && !viewLoading && (
          <div className="stats-grid">
            <StatCard icon="bi bi-arrow-repeat" tone="brand" value={summary.total} label="Attempts" />
            <StatCard icon="bi bi-trophy" tone="warning" value={summary.best} label="Best score" />
            <StatCard icon="bi bi-graph-up" tone="info" value={summary.average} label="Average score" />
            <StatCard icon="bi bi-percent" tone="success" value={pct(summary.bestPct)} label="Best percentage" />
          </div>
        )}

        <div className="table-card">
          {viewLoading ? (
            <LoadingState message="Loading attempts…" />
          ) : attemptsHistory.length === 0 ? (
            !viewError && (
              <div className="results-empty">
                <EmptyState icon="bi bi-inbox" title="No attempts yet" message="This student hasn't completed an attempt." />
              </div>
            )
          ) : (
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Attempt</th>
                    <th>Score</th>
                    <th>Percentage</th>
                    <th>Grade</th>
                    <th>Time taken</th>
                    <th>Submitted</th>
                    <th aria-label="Actions"></th>
                  </tr>
                </thead>
                <tbody>
                  {attemptsHistory.map((attempt) => (
                    <tr key={attempt.attempt_id}>
                      <td>
                        <span className="rank-chip">#{attempt.attempt_no}</span>
                      </td>
                      <td>
                        <strong className="text-strong">{num(attempt.score)}</strong>
                        <span className="text-muted"> / {num(attempt.max_marks)}</span>
                      </td>
                      <td>
                        <span className={`score-pill ${scoreTone(attempt.percentage)}`}>{pct(attempt.percentage)}</span>
                      </td>
                      <td>
                        <span className="badge badge-neutral">{attempt.grade || "—"}</span>
                      </td>
                      <td>{durationBetween(attempt.started_at, attempt.submitted_at)}</td>
                      <td className="nowrap">{formatDate(attempt.submitted_at, true)}</td>
                      <td>
                        <button type="button" className="btn-secondary btn-sm" onClick={() => handleReviewAnswers(attempt)}>
                          <i className="bi bi-file-earmark-text"></i> Review
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ===================== Leaderboard =====================
  if (view === "leaderboard" && selectedTestSeries) {
    const percentages = leaderboardData.map((r) => num(r.percentage));
    const top3 = leaderboardData.filter((s) => num(s.rank) >= 1 && num(s.rank) <= 3).slice(0, 3);

    return (
      <div className="results-page page">
        <div className="page-header">
          <div>
            <button type="button" className="back-link" onClick={() => goTo("list")}>
              <i className="bi bi-arrow-left"></i> All test series
            </button>
            <h2>{selectedTestSeries.title}</h2>
            <p>Leaderboard, based on each student's best attempt. Equal score and time share a rank.</p>
          </div>
          {leaderboardData.length > 0 && (
            <div className="header-actions">
              <button type="button" className="btn-secondary" onClick={handleQuestionStats}>
                <i className="bi bi-bar-chart"></i> Question analysis
              </button>
              <button type="button" className="btn-secondary" onClick={handleExport} disabled={exporting}>
                {exporting ? <span className="btn-spinner"></span> : <i className="bi bi-download"></i>} Export CSV
              </button>
            </div>
          )}
        </div>

        <ErrorAlert message={viewError} onRetry={() => handleShowLeaderboard(selectedTestSeries)} />

        {viewLoading ? (
          <LoadingState message="Loading leaderboard…" />
        ) : leaderboardData.length === 0 ? (
          !viewError && (
            <EmptyState
              icon="bi bi-trophy"
              title="No results yet"
              message="Once students complete this test series, their rankings will show here."
            />
          )
        ) : (
          <>
            <div className="stats-grid">
              <StatCard icon="bi bi-people" tone="brand" value={leaderboardData.length} label="Students ranked" />
              <StatCard icon="bi bi-arrow-up-circle" tone="success" value={pct(Math.max(...percentages))} label="Top score" />
              <StatCard
                icon="bi bi-bar-chart"
                tone="info"
                value={pct(percentages.reduce((a, b) => a + b, 0) / percentages.length)}
                label="Average"
              />
              <StatCard icon="bi bi-arrow-down-circle" tone="warning" value={pct(Math.min(...percentages))} label="Lowest score" />
            </div>

            {top3.length > 0 && (
              <div className="podium">
                {top3.map((student) => (
                  <button
                    type="button"
                    key={student.user_id}
                    className={`podium-card rank-${num(student.rank)}`}
                    onClick={() => handleViewAttempts(student)}
                  >
                    <span className="podium-medal">
                      <i className="bi bi-award-fill"></i>
                      <b>{num(student.rank)}</b>
                    </span>
                    <strong>{student.user_name}</strong>
                    <span className="podium-score">{pct(student.percentage)}</span>
                    <small>
                      {num(student.score)} / {num(student.max_marks ?? student.best_attempt?.summary?.max_marks)} marks
                    </small>
                  </button>
                ))}
              </div>
            )}

            <div className="table-card">
              <div className="table-scroll">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>Student</th>
                      <th>Score</th>
                      <th>Percentage</th>
                      <th>Time taken</th>
                      <th>Date</th>
                      <th aria-label="Actions"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaderboardData.map((student) => (
                      <tr key={student.user_id} className={num(student.rank) <= 3 ? "top-row" : ""}>
                        <td>
                          <span className={`rank-chip ${num(student.rank) <= 3 ? `rank-${num(student.rank)}` : ""}`}>
                            #{student.rank}
                          </span>
                        </td>
                        <td>
                          <div className="cell-user">
                            <span className="avatar">{(student.user_name || "?").charAt(0)}</span>
                            <div className="cell-stack">
                              <strong>{student.user_name}</strong>
                              <small>
                                {student.user_email || `ID ${student.user_id}`}
                                {num(student.total_attempts) > 1 ? ` · ${num(student.total_attempts)} attempts` : ""}
                              </small>
                            </div>
                          </div>
                        </td>
                        <td>
                          <strong className="text-strong">{num(student.score)}</strong>
                          <span className="text-muted"> / {num(student.max_marks ?? student.best_attempt?.summary?.max_marks)}</span>
                        </td>
                        <td>
                          <span className={`score-pill ${scoreTone(student.percentage)}`}>{pct(student.percentage)}</span>
                        </td>
                        <td>{student.time_taken_human || "—"}</td>
                        <td className="nowrap">{formatDate(student.submitted_at)}</td>
                        <td>
                          <button type="button" className="btn-secondary btn-sm" onClick={() => handleViewAttempts(student)}>
                            <i className="bi bi-clock-history"></i> Attempts
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  // ===================== Test series list =====================
  const query = searchQuery.trim().toLowerCase();
  const filteredTestSeries = testSeriesList.filter(
    (series) =>
      !query ||
      (series.title || "").toLowerCase().includes(query) ||
      (series.description || "").toLowerCase().includes(query)
  );
  const totalPages = Math.ceil(filteredTestSeries.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const currentTestSeries = filteredTestSeries.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  return (
    <div className="results-page page">
      <div className="page-header">
        <div>
          <h2>Leaderboards</h2>
          <p>Pick a test series to see how students ranked.</p>
        </div>
      </div>

      <ErrorAlert message={listError} onRetry={loadTestSeries} />

      <div className="toolbar">
        <SearchField value={searchQuery} onChange={setSearchQuery} placeholder="Search test series…" />
      </div>

      {loading ? (
        <LoadingState message="Loading test series…" />
      ) : filteredTestSeries.length === 0 ? (
        !listError && (
          <EmptyState
            icon={searchQuery ? "bi bi-search" : "bi bi-journal"}
            title={searchQuery ? "No matching test series" : "No test series yet"}
            message={searchQuery ? "Try a different search term." : "Create a test series to start collecting results."}
          />
        )
      ) : (
        <>
          <div className="grid-cards">
            {currentTestSeries.map((series) => {
              const totalQuestions =
                series.questions_count ??
                (Array.isArray(series.sections)
                  ? series.sections.reduce((sum, section) => sum + num(section.questions_count), 0)
                  : num(series.questions));
              const isPublished = series.visibility === "published";
              const isPremium = series.type === "premium";

              return (
                <article key={series.id} className="lb-card card card-hover">
                  <div className="lb-card-top">
                    <span className="lb-card-icon">
                      <i className="bi bi-trophy"></i>
                    </span>
                    <span className={`badge badge-dot ${isPublished ? "badge-success" : "badge-neutral"}`}>
                      {isPublished ? "Published" : "Draft"}
                    </span>
                  </div>
                  <h3 title={series.title}>{series.title}</h3>
                  <p className="lb-card-desc">{series.description || "No description added."}</p>
                  <div className="lb-card-meta">
                    {series.course && (
                      <span>
                        <i className="bi bi-mortarboard"></i> {series.course}
                      </span>
                    )}
                    <span>
                      <i className="bi bi-list-ol"></i> {totalQuestions} questions
                    </span>
                    <span>
                      <i className={`bi ${isPremium ? "bi-gem" : "bi-unlock"}`}></i> {isPremium ? "Premium" : "Free"}
                    </span>
                    <span>
                      <i className="bi bi-pencil-square"></i> {num(series.attempts_count)} attempts
                    </span>
                  </div>
                  <button type="button" className="btn-primary btn-block" onClick={() => handleShowLeaderboard(series)}>
                    <i className="bi bi-bar-chart-line"></i> View leaderboard
                  </button>
                </article>
              );
            })}
          </div>

          <div className="card lb-pagination">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(page) => {
                setCurrentPage(page);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              info={`Showing ${startIndex + 1}–${Math.min(startIndex + ITEMS_PER_PAGE, filteredTestSeries.length)} of ${
                filteredTestSeries.length
              } test series`}
            />
          </div>
        </>
      )}
    </div>
  );
}

export default Leaderboard;
